<!-- BusinessEntriesModal.vue -->
<template>
  <div class="modal fade" id="businessEntriesModal" tabindex="-1" aria-labelledby="businessEntriesModalLabel" aria-hidden="true">
    <div class="modal-dialog modal-xl">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title" id="businessEntriesModalLabel">
            Business Entries - {{ businessName }}
          </h5>
          <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
        </div>

        <div class="modal-body">
          <!-- Business Info Header -->
          <div class="bg-light p-3 mb-3 rounded" v-if="businessName">
            <div class="row">
              <div class="col-md-6">
                <h6 class="mb-1">Business Information</h6>
                <p class="mb-0"><strong>ID:</strong> {{ businessID }}</p>
                <p class="mb-0"><strong>Name:</strong> {{ businessName }}</p>
              </div>
              <div class="col-md-6 text-end">
                <button
                    @click="loadEntries()"
                    class="btn btn-sm btn-outline-primary"
                    :disabled="loading"
                >
                  <i class="bx bx-refresh" :class="{ 'bx-spin': loading }"></i> Refresh
                </button>
              </div>
            </div>
          </div>

          <!-- Loading state -->
          <div class="text-center py-5" v-if="loading">
            <div class="spinner-border text-primary" role="status">
              <span class="sr-only">Loading...</span>
            </div>
            <p class="mt-3">Loading entries...</p>
          </div>

          <!-- Entries table -->
          <div v-else-if="entries.length > 0" class="table-responsive">
            <table class="table table-striped table-hover">
              <thead class="table-dark">
              <tr>
                <th>Bill No</th>
                <th>Period</th>
                <th>Fee Description</th>
                <th>Amount</th>
                <th>Receipt</th>
                <th>Balance</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
              </thead>
              <tbody>
              <tr v-for="(entry, index) in entries" :key="index">
                <td class="text-nowrap">
                  {{ entry.billNo }}<br>
                  <small class="text-muted">{{ entry.type || 'NEW' }}</small>
                </td>
                <td class="text-nowrap">
                  <small>From: {{ formatDate(entry.startDate) }}</small><br>
                  <small>To: {{ formatDate(entry.endDate) }}</small><br>
                  <span class="badge bg-info">{{ entry.duration }}</span>
                </td>
                <td>
                  {{ entry.feeDescription }}<br>
                  <small class="text-muted">{{ entry.subCountyName }}, {{ entry.wardName }}</small>
                </td>
                <td class="text-nowrap">
                  <strong>KES {{ numberFormat(entry.billTotal || 0) }}</strong>
                </td>
                <td class="text-nowrap">
                  <span class="text-success">KES {{ numberFormat(entry.receiptAmount || 0) }}</span>
                </td>
                <td class="text-nowrap">
                    <span :class="entry.billBalance > 0 ? 'text-danger' : 'text-success'">
                      KES {{ numberFormat(entry.billBalance || 0) }}
                    </span>
                </td>
                <td>
                    <span class="badge" :class="getStatusClass(entry.stageStatus || entry.status)">
                      {{ entry.stageStatus || entry.status }}
                    </span><br>
                  <small class="text-muted">{{ entry.renewalStatus }}</small>
                </td>
                <td class="text-nowrap">
                  <button
                      v-if="entry.printable === 'true' && entry.stageStatus === 'Approved'"
                      @click="printPermit(entry)"
                      class="btn btn-sm btn-success"
                      title="Print Permit"
                  >
                    <i class="bx bx-printer"></i>
                  </button>
                  <button
                      @click="viewDetails(entry)"
                      class="btn btn-sm btn-info ms-1"
                      title="View Details"
                  >
                    <i class="bx bx-show"></i>
                  </button>
                </td>
              </tr>
              </tbody>
              <tfoot class="table-dark">
              <tr>
                <th colspan="3">Total ({{ entries.length }} entries)</th>
                <th>KES {{ numberFormat(totalBillAmount) }}</th>
                <th>KES {{ numberFormat(totalReceiptAmount) }}</th>
                <th>KES {{ numberFormat(totalBalanceAmount) }}</th>
                <th colspan="2"></th>
              </tr>
              </tfoot>
            </table>
          </div>

          <!-- No entries -->
          <div v-else class="text-center py-5">
            <img :src="'./assets/images/no-results.png'" alt="No entries" class="img-fluid mb-3" style="max-width: 200px;">
            <h5>No Entries Found</h5>
            <p class="text-muted">No entries have been recorded for this business yet.</p>
          </div>
        </div>

        <div class="modal-footer">
          <!-- Pagination controls -->
          <div class="d-flex align-items-center justify-content-between w-100" v-if="entries.length > 0">
            <div class="d-flex align-items-center">
              <label class="me-2 mb-0">Rows per page:</label>
              <select class="form-select form-select-sm" v-model="pageSize" style="width: auto;">
                <option value="10">10</option>
                <option value="25">25</option>
                <option value="50">50</option>
                <option value="100">100</option>
              </select>
            </div>

            <nav aria-label="Page navigation">
              <ul class="pagination pagination-sm mb-0">
                <li class="page-item" :class="{ disabled: currentPage === 1 }">
                  <a class="page-link" @click="prevPage" href="javascript:void(0)">Previous</a>
                </li>
                <li class="page-item" v-for="page in displayedPages" :key="page" :class="{ active: currentPage === page, disabled: page === '...' }">
                  <a class="page-link" @click="page !== '...' && goToPage(page)" href="javascript:void(0)">{{ page }}</a>
                </li>
                <li class="page-item" :class="{ disabled: currentPage === totalPages }">
                  <a class="page-link" @click="nextPage" href="javascript:void(0)">Next</a>
                </li>
              </ul>
            </nav>
          </div>

          <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Close</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { execute, trade } from "@/api";
import moment from 'moment';

export default {
  name: "BusinessEntriesModal",

  data() {
    return {
      entries: [],
      loading: false,
      businessID: null,
      businessName: '',
      currentPage: 1,
      pageSize: 10,
      totalItems: 0,
      modalInstance: null
    };
  },

  computed: {
    totalPages() {
      return Math.ceil(this.totalItems / this.pageSize);
    },

    totalBillAmount() {
      return this.entries.reduce((sum, entry) => {
        return sum + (parseFloat(entry.billTotal) || 0);
      }, 0);
    },

    totalReceiptAmount() {
      return this.entries.reduce((sum, entry) => {
        return sum + (parseFloat(entry.receiptAmount) || 0);
      }, 0);
    },

    totalBalanceAmount() {
      return this.entries.reduce((sum, entry) => {
        return sum + (parseFloat(entry.billBalance) || 0);
      }, 0);
    },

    displayedPages() {
      return this.pagination(this.currentPage, this.totalPages, 5);
    }
  },

  watch: {
    pageSize() {
      this.currentPage = 1;
      if (this.businessID) {
        this.loadEntries();
      }
    }
  },

  mounted() {
    // Initialize Bootstrap modal
    const modalElement = document.getElementById('businessEntriesModal');
    this.modalInstance = new window.bootstrap.Modal(modalElement);
  },

  methods: {
    showModal(business) {
      this.businessID = business.businessID;
      this.businessName = business.businessName;
      this.entries = [];
      this.currentPage = 1;

      this.modalInstance.show();
      this.loadEntries();
    },

    loadEntries() {
      this.loading = true;

      const data = new FormData();
      data.append("function", "getBusinessEntries");
      data.append("businessID", this.businessID);
      data.append("page", this.currentPage);
      data.append("rows_per_page", this.pageSize);

      execute(data, trade)
          .then((res) => {
            this.loading = false;

            if (res.data.success) {
              this.entries = res.data.data.entries || [];
              this.totalItems = res.data.data.count || 0;
            } else {
              this.$emit('show-message', res.data.message || 'Failed to load entries', 'error');
            }
          })
          .catch((error) => {
            this.loading = false;
            this.$emit('show-message', 'Error loading entries: ' + error.message, 'error');
          });
    },

    formatDate(date) {
      if (!date) return '-';
      return moment(date).format('DD MMM YYYY hh:mm A');
    },

    numberFormat(num) {
      return new Intl.NumberFormat("en-GB").format(num);
    },

    getStatusClass(status) {
      const statusClasses = {
        'approved': 'bg-success',
        'approval': 'bg-info',
        'pending': 'bg-warning',
        'rejected': 'bg-danger',
        'paid': 'bg-success',
        'unpaid': 'bg-danger'
      };
      return statusClasses[status?.toLowerCase()] || 'bg-secondary';
    },

    printPermit(entry) {
      // Emit event to parent to handle printing
      this.$emit('print-permit', entry);
      console.log('Print permit for bill:', entry.billNo);
    },

    viewDetails(entry) {
      // Show additional details in a separate modal or expand view
      console.log('View details for entry:', entry);

      // You can emit an event or show more details here
      alert(`
        Business: ${entry.businessName}
        Owner: ${entry.owner} (${entry.ownerPhone})
        Registered by: ${entry.registeredBy}
        Issued on: ${this.formatDate(entry.dateOfIssue)}
        Fiscal Year: ${entry.fiscalYear}
        Zone: ${entry.zone || 'N/A'}
        Remarks: ${entry.remarks || 'None'}
      `);
    },

    pagination(currentPage, totalPages, maxVisible) {
      let pages = [];
      let start = Math.max(1, currentPage - Math.floor(maxVisible / 2));
      let end = Math.min(totalPages, start + maxVisible - 1);

      if (end - start + 1 < maxVisible) {
        start = Math.max(1, end - maxVisible + 1);
      }

      if (start > 1) {
        pages.push(1);
        if (start > 2) pages.push('...');
      }

      for (let i = start; i <= end; i++) {
        pages.push(i);
      }

      if (end < totalPages) {
        if (end < totalPages - 1) pages.push('...');
        pages.push(totalPages);
      }

      return pages;
    },

    prevPage() {
      if (this.currentPage > 1) {
        this.currentPage--;
        this.loadEntries();
      }
    },

    nextPage() {
      if (this.currentPage < this.totalPages) {
        this.currentPage++;
        this.loadEntries();
      }
    },

    goToPage(page) {
      if (page !== this.currentPage && page !== '...') {
        this.currentPage = page;
        this.loadEntries();
      }
    }
  }
};
</script>

<style scoped>
.modal-xl {
  max-width: 90%;
}

.badge {
  font-size: 0.875rem;
  padding: 0.25rem 0.5rem;
}

.pagination {
  margin-bottom: 0;
}

.page-link {
  cursor: pointer;
}

.page-item.disabled .page-link {
  cursor: not-allowed;
}

.btn-sm {
  padding: 0.25rem 0.5rem;
  font-size: 0.875rem;
}

.table td {
  vertical-align: middle;
}

/* Spinning animation for refresh icon */
@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

.bx-spin {
  animation: spin 1s linear infinite;
}

/* Make the modal more readable */
.table-responsive {
  max-height: 60vh;
  overflow-y: auto;
}

.bg-light {
  background-color: #f8f9fa !important;
}
</style>